/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

'use client';

import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useKPIContext } from '@/providers/KpiProvider';
import { useLanguage } from '@/providers/LanguageProvider';
import { request } from '@/services/request';
import { convertToUTCDate } from '@/utils/convertToUTCDate';
import { getIconByUnit } from '@/utils/measureIcons';
import EvalButton from '@/ux-common/src/components/evals/eval-button';
import { formatNumberByLocale } from '@/ux-common/src/lib/utils';
import { EvalJobType } from '@/ux-common/src/types/api';
import Error from 'public/shared-icons/Error.svg';
import { NodeMetaData, PinnedItem, SankeyNodeAction } from 'types/sankey';

import { useAppDetails } from '@/hooks/useAppDetails';
import { useEvals } from '@/hooks/useEvals';
import { useMeasuresCache } from '@/hooks/useMeasuresCache';
import { useSankeyToPromptsNavigation } from '@/hooks/useSankeyToPromptsNavigation';
import { useSankeyToTracesNavigation } from '@/hooks/useSankeyToTracesNavigation';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Icons as PortalIcons, System } from '@/components/icons';
import { NotFoundMessage } from '@/components/not-found';
import { RectNode } from '@/components/sanky/sankey-rect';
import BasicLoader from '@/components/spinner/basic-loader';

import {
  getPresetLabelToNameMap,
  getPresetRange,
  usePresets,
} from '../prompts/date-range-picker/calendar-helper/presets';
import { truncateTraceId } from '../traces/traceUtils';
import { NodeItem } from './node-item';

interface NodeDetailsProps {
  selectedNode: RectNode | null;
  appName: string;
  onClear: () => void;
  factName: string;
  rootNodeKey: string;
  setEvaluateOpen?: Dispatch<SetStateAction<boolean>>;
  workflowNames?: string | null;
  selectedFilterFact?: string | null;
  selectedFilterFactIDs?: string | null;
}

const NodeDetails: React.FC<NodeDetailsProps> = ({
  selectedNode,
  appName,
  onClear,
  factName,
  rootNodeKey,
  setEvaluateOpen,
  selectedFilterFact,
  selectedFilterFactIDs,
}) => {
  const searchParams = useSearchParams();
  const { messages } = useLanguage();
  const presets = usePresets();
  const { measuresCache, setMeasuresCache } = useMeasuresCache();
  const { nodeKey, setNodeKey } = useKPIContext();
  const sankeyConstants = messages?.Sankey;
  const { handleTraceClick } = useSankeyToTracesNavigation();
  const { handlePromptClick } = useSankeyToPromptsNavigation();
  const [nodeDetails, setNodeDetails] = useState<NodeMetaData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMeasuresError, setHasMeasuresError] = useState<boolean>(false);
  const [hasEmptyMeasures, setHasEmptyMeasures] = useState<boolean>(false);
  const [pinnedItems, setPinnedItems] = useState<PinnedItem[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('pinnedItems') || '[]');
    } catch {
      return [];
    }
  });
  const [myPinnedItems, setMyPinnedItems] = useState<PinnedItem[]>([]);
  const {
    fetchCategorizedErrors,
    categorizedErrors,
    fetchRange,
    responseError,
  } = useKPIContext();
  const [isErrorCategoriesLoading, setIsErrorCategoriesLoading] =
    useState(false);
  const workflowNamesFromURL = searchParams.get('workflow_names');

  const { state } = useEvals();

  const evalJobs: EvalJobType[] = state.evalJobs;

  const runningJobsCount = evalJobs.filter(
    (cur) => cur.status === 'RUNNING'
  ).length;

  const headlineUnknown = messages?.General?.unknown;

  const { viewBy } = useKPIContext();
  // map Replay Tests to Quality view
  const targetView = viewBy === 'Replay Tests' ? 'Quality' : viewBy;

  const currentViewActions: SankeyNodeAction[] =
    (selectedNode?.views?.find((view) => Array.isArray(view[targetView]))?.[
      targetView
    ] as SankeyNodeAction[]) || [];

  const inspectTracesAction = currentViewActions.find(
    (t) => t.action === messages?.Sankey?.dropdown?.inspect_traces
  );

  const inspectTracesRedirect = inspectTracesAction?.redirect
    ? {
        ...inspectTracesAction.redirect,
        factName: factName,
        filterFact: selectedFilterFact,
        filterFactIDs: selectedFilterFactIDs,
      }
    : undefined;

  // Remove "Inspect Prompts" option when factName is tool executions
  // Remove "Evaluate" option for quality view nodes
  const filteredViewActions = currentViewActions.filter(
    (action) =>
      !(
        action.action === messages?.Sankey?.dropdown?.inspect_prompts &&
        factName === 'tool_operations'
      ) && action.action !== messages?.Evaluation?.evaluate
  );

  useEffect(() => {
    localStorage.setItem('pinnedItems', JSON.stringify(pinnedItems));
    const myItems = pinnedItems.filter(
      (item) => item.appName === appName && item.factName === factName
    );
    setMyPinnedItems(myItems);
  }, [pinnedItems, factName]);

  const currentNodeKeyRef = useRef<string>('');
  const appDetails = useAppDetails();
  const appData = appDetails?.data;

  const prepareMeasuresEndpoint = (
    apiRange: { from: Date | null; to: Date | null },
    nodeKey: string
  ) => {
    if (!nodeKey) return null;

    const params = new URLSearchParams();

    if (apiRange.from && apiRange.to) {
      params.append('start_time', apiRange.from.toISOString());
      params.append('end_time', apiRange.to.toISOString());
    }

    const query = params.toString();

    return `insights/apps/${appName}/facts/${factName}/measures/${nodeKey}${
      query ? `?${query}` : ''
    }`;
  };

  const fetchNodeMetaData = async (apiRange: {
    from: Date | null;
    to: Date | null;
  }) => {
    try {
      if (!nodeKey || nodeKey === '') return;
      currentNodeKeyRef.current = nodeKey;

      setIsLoading(true);
      setHasMeasuresError(false);
      if (measuresCache.has(nodeKey)) {
        setNodeDetails(measuresCache.get(nodeKey) || []);
        setIsLoading(false);
        return;
      } else {
        const endpoint = prepareMeasuresEndpoint(apiRange, nodeKey);
        if (!endpoint) return;

        const data = await request.get({
          endpoint,
        });

        if (nodeKey === currentNodeKeyRef.current) {
          setNodeDetails(data[0].measures);
          const cacheCopy = new Map(measuresCache);
          cacheCopy.set(nodeKey, data[0].measures);
          setMeasuresCache(cacheCopy);
        }
      }
    } catch (error) {
      console.log('error', error);
      // clear stale node data
      if (nodeKey === currentNodeKeyRef.current) {
        setNodeDetails([]);
        setHasMeasuresError(true);
      }
    } finally {
      if (nodeKey === currentNodeKeyRef.current) {
        setIsLoading(false);
      }
    }
  };

  const fetchErrorGroup = async () => {
    setIsErrorCategoriesLoading(true);
    try {
      // Fetch categorized errors for the selected node and current time range
      await fetchCategorizedErrors(
        appName,
        selectedNode?.key || '',
        fetchRange,
        false,
        workflowNamesFromURL,
        selectedFilterFact,
        selectedFilterFactIDs
      );
    } catch (error) {
      console.error('Failed to fetch categorized errors:', error);
    } finally {
      setIsErrorCategoriesLoading(false);
    }
  };

  useEffect(() => {
    setNodeKey('');
    setMeasuresCache(new Map());
  }, [searchParams]);

  useEffect(() => {
    // Clear previously categorized errors when node or date range changes.
    fetchCategorizedErrors(appName, selectedNode?.key || '', fetchRange, true);

    const daysParam = searchParams.get('days');
    const startParam = searchParams.get('start');
    const endParam = searchParams.get('end');
    let apiRange;

    if (daysParam === 'all') {
      apiRange = {
        from: null,
        to: null,
      };
    } else if (startParam && endParam) {
      apiRange = {
        from: convertDateToUTC(new Date(startParam)),
        to: convertDateToUTC(new Date(endParam)),
      };
    } else {
      const presetMap = getPresetLabelToNameMap(presets);
      const defaultPreset = presetMap[appData?.default_time_range] || 'last7';
      const range = getPresetRange(defaultPreset, presets);
      //fallback.
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
      const now = new Date();

      apiRange = {
        from: convertDateToUTC(range.from || sevenDaysAgo),
        to: convertDateToUTC(range.to || now),
      };
    }

    fetchNodeMetaData(apiRange);
  }, [nodeKey, fetchRange]);

  // check if all measures are empty to show "No measures found" message instead of empty list
  useEffect(() => {
    if (nodeDetails.length > 0 && !isLoading && !hasMeasuresError) {
      const isEmptyMeasures = nodeDetails.every(
        (measure) => measure.value === 0
      );
      setHasEmptyMeasures(isEmptyMeasures);
    }
  }, [nodeDetails, isLoading, hasMeasuresError]);

  const addPinnedItem = (item: PinnedItem) => {
    if (myPinnedItems.length == 0) {
      setPinnedItems((prev) => [...prev, item]);
    } else {
      // remove items which belong to current application and include the new one
      setPinnedItems((prev) =>
        prev.filter((pinnedItem) => pinnedItem.appName !== appName).concat(item)
      );
    }
  };

  const removePinnedItem = (nodeName: string, label: string) => {
    setPinnedItems((prev) =>
      prev.filter(
        (item) =>
          !(
            item.nodeName === nodeName &&
            item.label === label &&
            item.appName === appName &&
            item.factName === factName
          )
      )
    );
  };

  const showUnit = (unit?: string) => {
    return unit === 'Seconds' ? 'Secs' : unit === 'Percentage' ? '%' : '';
  };

  const showPinnedItems = () => (
    <div className="space-y-4">
      {myPinnedItems.map((item) => (
        <NodeItem
          key={`${item.nodeName}-${item.label}`}
          icon={getIconByUnit(item.unit, item.label)}
          nodeName={item.nodeName}
          title={item.label}
          data={item?.value ? formatNumberByLocale(item.value) : 0}
          unit={showUnit(item.unit)}
          onPinToggle={() => removePinnedItem(item.nodeName, item.label)}
          isPinned={true}
        />
      ))}
    </div>
  );

  const showUnpinnedItems = () => {
    const unpinnedItems =
      nodeDetails?.filter(
        (item) =>
          !myPinnedItems.find(
            (pinnedItem) =>
              pinnedItem.nodeKey === selectedNode?.key &&
              pinnedItem.label === item.label &&
              pinnedItem.factName === factName
          )
      ) || [];

    return (
      <div className="space-y-4">
        {unpinnedItems.map((item) => (
          <NodeItem
            key={item.label}
            icon={getIconByUnit(item.unit, item.label)}
            title={item.label}
            data={item?.value ? formatNumberByLocale(item.value) : 0}
            unit={showUnit(item.unit)}
            onPinToggle={() =>
              addPinnedItem({
                nodeName: selectedNode?.name || '',
                nodeKey: nodeKey,
                label: item.label,
                value: item.value,
                unit: item.unit || '',
                appName: appName,
                factName: factName,
              })
            }
            isPinned={false}
            hidePin={myPinnedItems.length >= 3}
            tooltipMessage={
              myPinnedItems.length == 1
                ? sankeyConstants?.measures.tooltip_message
                : ''
            }
          />
        ))}
      </div>
    );
  };

  const backIcon = PortalIcons.GoBackIcon('');
  const isErrorNode = JSON.stringify(selectedNode?.views ?? []).includes(
    'Categorize Errors'
  );

  return (
    <div className="flex h-[433px] w-1/5 flex-col items-start space-y-6">
      {/* pinned items: first child */}
      {myPinnedItems.length > 0 && (
        <>
          <div className="w-full">{showPinnedItems()}</div>
          <Separator className="bg-line-tertiary" orientation="horizontal" />
        </>
      )}

      {/* second child */}
      <div className="flex w-full flex-1 flex-col gap-[18px] overflow-hidden">
        {/* inner child - 1 */}
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <h3 className="text-[19px] text-body-secondary">
              {selectedNode?.name}
            </h3>
            {selectedNode != null && selectedNode?.key !== rootNodeKey && (
              <button
                className="flex cursor-pointer items-center justify-center"
                onClick={onClear}
              >
                {backIcon}
              </button>
            )}
          </div>

          <div className="group flex items-center">
            <button
              className={`flex items-baseline gap-1 ${factName === messages?.General.traces?.toLowerCase() ? 'cursor-pointer' : 'cursor-default'}`}
              onClick={
                factName === messages?.General.traces?.toLowerCase()
                  ? () =>
                      handleTraceClick(
                        inspectTracesRedirect,
                        selectedNode?.key,
                        workflowNamesFromURL
                      )
                  : undefined
              }
            >
              <p className="text-[28px] leading-[28px] text-link-active">
                {formatNumberByLocale(selectedNode?.value)}
              </p>
              {selectedNode != null && selectedNode.key !== factName && (
                <span className="text-[13px] text-[#34BCAF]">
                  {
                    messages?.General?.[
                      factName as keyof typeof messages.General
                    ]
                  }
                </span>
              )}
            </button>

            <div className="flex items-center">
              {filteredViewActions.length > 0 &&
                !selectedNode?.key.endsWith('.Other') && (
                  <>
                    <div className="block">
                      {selectedNode != null && (
                        <Separator
                          className="mx-2 h-6 bg-line-tertiary"
                          orientation="vertical"
                        />
                      )}
                    </div>
                    <DropdownMenu>
                      {isErrorCategoriesLoading ? (
                        <div className="flex items-center gap-1 text-btn-label text-form-input-disabled">
                          <span className="mr-1">
                            {sankeyConstants?.dropdown?.categorizing}
                          </span>
                          <BasicLoader className="size-4" />
                        </div>
                      ) : (
                        <DropdownMenuTrigger asChild>
                          <button className="flex cursor-pointer items-center gap-1 rounded-sm py-1 pl-2 pr-3 text-btn-label text-body-primary hover:bg-white/[0.08] data-[state=open]:bg-white/[0.08]">
                            {sankeyConstants?.dropdown?.investigate}
                            {System.ChevronDownIcon(
                              'size-4 text-body-primary mt-[1px]'
                            )}
                          </button>
                        </DropdownMenuTrigger>
                      )}
                      <DropdownMenuContent
                        align="start"
                        className="z-50 cursor-pointer rounded-none bg-white p-0 text-form-input-text text-[#201F1E]"
                      >
                        {filteredViewActions.map((action: SankeyNodeAction) => (
                          <DropdownMenuItem
                            key={action.action}
                            className="cursor-pointer hover:bg-gray-100"
                            onClick={() => {
                              if (
                                action.action === 'Inspect Traces' &&
                                action.redirect
                              ) {
                                handleTraceClick(
                                  {
                                    ...action.redirect,
                                    factName: factName,
                                    filterFact: selectedFilterFact,
                                    filterFactIDs: selectedFilterFactIDs,
                                  },
                                  selectedNode?.key,
                                  workflowNamesFromURL
                                );
                              } else if (
                                action.action === 'Inspect Prompts' &&
                                action.redirect
                              ) {
                                handlePromptClick(
                                  { ...action.redirect, page: factName }, // include current fact_name
                                  selectedNode?.key,
                                  workflowNamesFromURL
                                );
                              } else if (
                                action.action === 'Categorize Errors'
                              ) {
                                fetchErrorGroup();
                              }
                            }}
                          >
                            {action.action}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
            </div>
          </div>
        </div>
        <EvalButton
          title={messages?.Evaluation.evaluate ?? ''}
          tooltipDisabled={messages?.Evaluation.disabled_tooltip ?? ''}
          evaluateTooltip={messages?.Evaluation.evaluate_tooltip ?? ''}
          setEvaluateOpen={setEvaluateOpen}
          runningJobsCount={runningJobsCount}
        />

        {/* inner child - 2 */}
        {isLoading ? (
          <div className="flex h-12 items-center justify-start gap-2">
            <div>
              <Skeleton className="animate-pulse-custom h-12 w-12 rounded-full bg-[#8C9EA1]" />
            </div>
            <div className="flex flex-1 flex-col items-start gap-2">
              <Skeleton className="animate-pulse-custom h-2 w-1/3 rounded-full bg-[#8C9EA1]" />
              <Skeleton className="animate-pulse-custom h-[15px] w-2/3 rounded-full bg-[#8C9EA1]" />
            </div>
          </div>
        ) : (
          <div className="flex-1 space-y-4 overflow-y-auto pr-2">
            {hasMeasuresError || hasEmptyMeasures ? (
              <NotFoundMessage
                title="No measures found"
                message="No measures available for the selected node"
                outerDivClassName="my-20"
              />
            ) : (
              showUnpinnedItems()
            )}

            {isErrorNode && (
              <div className="mt-4">
                {isErrorCategoriesLoading ? (
                  <div className="flex flex-col space-y-4">
                    <Skeleton className="animate-pulse-custom h-12 rounded-md bg-[#8C9EA1]" />
                    <Skeleton className="animate-pulse-custom h-12 rounded-md bg-[#8C9EA1]" />
                  </div>
                ) : (
                  categorizedErrors &&
                  categorizedErrors.length > 0 && (
                    <>
                      <Separator
                        className="bg-line-tertiary"
                        orientation="horizontal"
                      />

                      <p className="mb-3 mt-4 text-body-sm text-form-input-disabled">
                        {sankeyConstants?.errors_by_category}
                      </p>
                      <div className="space-y-4">
                        {categorizedErrors?.map(
                          (errorGroup: {
                            id: string;
                            headline?: string;
                            count?: number;
                            sample?: { trace_id: string; content: string }[];
                          }) => {
                            // Calculate headline just below map line
                            const headline =
                              errorGroup?.headline ??
                              errorGroup?.sample?.[0]?.content
                                .split(' ')
                                .slice(0, 4)
                                .join(' ') ??
                              headlineUnknown ??
                              '';
                            const cleanedHeadline = headline.replace(
                              /^['"]|['"]$/g,
                              ''
                            );
                            const words = cleanedHeadline.split(' ');

                            return (
                              <div key={errorGroup?.id} className="rounded-md">
                                <div className="text-body text-body-subtle">
                                  <span className="inline-block">
                                    {words.length > 1 ? (
                                      <>
                                        {words.slice(0, -1).join(' ')}{' '}
                                        <span className="inline-flex items-center whitespace-nowrap">
                                          {words[words.length - 1]}
                                          <span className="mb-0.5 ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#40565A] px-1 align-middle text-body-sm font-medium text-body-primary">
                                            {errorGroup?.count}
                                          </span>
                                        </span>
                                      </>
                                    ) : (
                                      <span className="inline-flex items-center whitespace-nowrap">
                                        {cleanedHeadline}
                                        <span className="mb-0.5 ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#40565A] px-1 align-middle text-body-sm font-medium text-body-primary">
                                          {errorGroup?.count}
                                        </span>
                                      </span>
                                    )}
                                  </span>
                                </div>
                                {errorGroup.sample &&
                                  errorGroup.sample.length > 0 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <Link
                                          href={`/en/apps/${appName}/traces?trace_id=${errorGroup.sample[0].trace_id}&activeTab=search`}
                                          passHref
                                        >
                                          <TooltipTrigger className="text-kpi-label text-[#257C9D] underline underline-offset-2 hover:cursor-pointer">
                                            {
                                              sankeyConstants?.dropdown
                                                ?.inspect_sample
                                            }
                                          </TooltipTrigger>
                                        </Link>
                                        <TooltipContent
                                          side="bottom"
                                          className="rounded border-transparent bg-[#07181E] text-[12px] text-body-primary"
                                        >
                                          {
                                            sankeyConstants?.dropdown
                                              ?.trace_id_tooltip
                                          }{' '}
                                          {errorGroup.sample[0].trace_id &&
                                            truncateTraceId(
                                              errorGroup.sample[0].trace_id
                                            )}
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                              </div>
                            );
                          }
                        )}
                      </div>
                    </>
                  )
                )}
              </div>
            )}

            {responseError ? (
              <div>
                <Separator className="bg-line-tertiary" />
                <div className="mt-4 flex items-center gap-1">
                  <Image
                    className="size-6 self-start"
                    src={Error}
                    alt="Error Icon"
                  />
                  <div>
                    <p className="text-body text-notification-error-dark">
                      {responseError?.data?.error?.message}
                    </p>
                    <button
                      onClick={() => fetchErrorGroup()}
                      className="text-kpi-label text-[#257C9D] underline underline-offset-2 hover:cursor-pointer"
                    >
                      {sankeyConstants?.try_again}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export { NodeDetails };

function convertDateToUTC(date: Date) {
  const utcString = convertToUTCDate(date);
  return utcString === null ? utcString : new Date(utcString);
}
